/* eslint-disable react/display-name */
import "../styles/index.less";
import { useMatch, useParams } from "react-router-dom";
import React, { CSSProperties, useState } from "react";
import { Layout, Spin } from "antd";
import ExistingProject from "./components/EditingRouter/EditingRouterProvider";
import ActionsBar from "./components/ActionsBar";
import Search, { SearchContext, useSearchReducer } from "./components/Search";
import { usePages } from "./components/Page";
import SubRouteNotImported from "./components/EditingRouter/subroutes/SubRouteNotImported";

import { ExtendedDefinitionKind, ImportStatus } from "../../../../__generated__/globalTypes";
import { useSubToProject } from "../Project/api/api";
import { useIsContentAdministrator } from "../../../identity";
import TextUnitExtractor from "./components/BatchColorationUpdates/TextUnitExtractor";
import { ExtractSyllablesWithRegex_extractSyllablesWithRegex } from "./api/__generated__/ExtractSyllablesWithRegex";
import { ExtractPhonemesWithRegex_extractPhonemesWithRegex } from "./api/metadata/static/__generated__/ExtractPhonemesWithRegex";
import { PageNavigationProvider } from "./components/Page/NavigationProvider";
const { Content } = Layout;

export const Spinner = ({ style }: { style?: CSSProperties }) => (
  <div
    style={{
      ...style,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }}>
    <Spin />
  </div>
);

export type Definition = {
  id: string;
  kind: ExtendedDefinitionKind;
  definition: string;
  imageName: string | null;
  insertOrder: number;
};

export default React.memo(() => {
  const matchesBookEdit = useMatch("/editor/books/edit/:bookId/*");
  const { bookId } = useParams<{ bookId: string }>();
  if (!bookId) {
    throw new Error("No bookId provided");
  }
  const isContentAdmin = useIsContentAdministrator();
  const search = useSearchReducer(bookId);
  const { data, loading: isProjectLoading } = useSubToProject({
    id: bookId,
    withAudioInfo: false,
    withInfo: false,
  });

  const [isExtractorOpen, setIsExtractorOpen] = useState(false);
  const [lastPathSegment, setLastPathSegment] = useState<string>("");
  const [isRegexEnabled, setIsRegexEnabled] = useState<boolean>(() => {
    return localStorage.getItem("regex-tool-enabled") === "true";
  });

  // Check for enableRegex URL param on mount
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("enableRegex") === "true") {
      localStorage.setItem("regex-tool-enabled", "true");
      setIsRegexEnabled(true);
      params.delete("enableRegex");
      const newUrl = `${window.location.pathname}${
        params.toString() ? `?${params.toString()}` : ""
      }`;
      window.history.replaceState(window.history.state, "", newUrl + window.location.hash);
    }
  }, []);

  // Determine which tab to open based on path
  React.useEffect(() => {
    if (matchesBookEdit) {
      const pathSegments = window.location.pathname.split("/");
      const lastSegment = pathSegments[pathSegments.length - 1];
      setLastPathSegment(lastSegment);
    }
  }, [matchesBookEdit]);

  // Determine initial tab based on path
  const getInitialTab = () => {
    if (lastPathSegment === "syllable") {
      return "syllables";
    } else if (lastPathSegment === "phoneme") {
      return "phonemes";
    }
    return "syllables"; // Default
  };

  const handleSyllableClick = (syllable: ExtractSyllablesWithRegex_extractSyllablesWithRegex) => {
    // Handle syllable click
    console.log("Syllable clicked:", syllable);
  };

  const handlePhonemeClick = (phoneme: ExtractPhonemesWithRegex_extractPhonemesWithRegex) => {
    // Handle phoneme click
    console.log("Phoneme clicked:", phoneme);
  };

  const project = data?.project[0];

  const { pages, loading } = usePages(
    bookId!,
    isProjectLoading || project?.importStatus !== "DONE",
  );

  if (project?.importStatus === ImportStatus.FAILED) {
    return <SubRouteNotImported failed />;
  }

  if (
    project?.importStatus === ImportStatus.WAITING_FOR_CONTENT ||
    project?.importStatus === ImportStatus.IMPORTING_CONTENT
  ) {
    return <SubRouteNotImported notImportedYet />;
  }

  if (!project || loading || isProjectLoading) {
    return <SubRouteNotImported loading />;
  }

  return (
    <PageNavigationProvider key={bookId} pages={pages}>
      <SearchContext.Provider value={search.state}>
        <Layout id="project-root" style={{ overflow: "auto" }}>
          <Content
            className="site-layout-background"
            style={{
              margin: 0,
              flex: 1,
            }}>
            <ExistingProject pages={pages} loading={loading} projectId={bookId} />
          </Content>
        </Layout>
        {!!pages.length && (
          <>
            <Search
              pages={pages}
              bookId={bookId}
              searchMode={search.state.searchMode}
              setSearchMode={search.send.setSearchMode}
              isOpen={search.state.isOpen}
              close={search.send.close}
              results={search.results}
              loading={search.loading}
              totalElements={search.total}
              textQuery={search.state.searchString}
              setTextQuery={search.send.setSearchString}
            />

            {matchesBookEdit && (isContentAdmin || isRegexEnabled) ? (
              <TextUnitExtractor
                projectId={bookId}
                stringVersion={project.stringVersion}
                isOpen={isExtractorOpen}
                close={() => setIsExtractorOpen(false)}
                onSyllableClick={handleSyllableClick}
                onPhonemeClick={handlePhonemeClick}
                initialTab={getInitialTab()}
              />
            ) : null}

            {matchesBookEdit ? (
              <ActionsBar
                loading={loading}
                projectId={bookId}
                isSearchOpen={search.state.isOpen}
                toggleEdition={() => setIsExtractorOpen((isOpen) => !isOpen)}
                toggleSearch={search.send.toggle}
              />
            ) : null}
          </>
        )}
      </SearchContext.Provider>
    </PageNavigationProvider>
  );
});
